import { cloneDeep } from 'lodash';
import { useEffect, useMemo } from 'react';

import { Position } from '@work4all/models/lib/Classes/Position.entity';

import {
  ErpConversionGroupByType,
  ErpObject,
  SelectedPosition,
} from '../types';

import { useApplyArticlePricesOnPositions } from './use-apply-article-prices-on-positions';

/**
 * Custom hook to apply patches to ERP data and update the state by patch.
 * Listen to fetch data changes.
 *
 * @param fetchedData - The current fetched data.
 * @param positionPatch - An array of objects containing changes to be applied.
 * @param setPatchToData - The setter function for the data state.
 */

export const useErpDataPatcher = (
  fetchedData: ErpObject[],
  positionPatch: SelectedPosition[] | null,
  setPatchToData: React.Dispatch<React.SetStateAction<ErpObject[]>>,
  groupByType?: ErpConversionGroupByType,
  isEditMode?: boolean
): void => {
  const positions = useMemo(
    () => fetchedData.flatMap((x) => x.positionList),
    [fetchedData]
  );
  const { applyArticlePricesOnPositions, loading } =
    useApplyArticlePricesOnPositions({
      positions,
      groupByType,
      sourcePrices: !isEditMode,
    });

  // This effect applies the patches whenever positionPatch changes.
  useEffect(() => {
    if (!positionPatch && !loading) {
      setPatchToData((prev) => {
        // Create a deep clone of the previous state to ensure immutability.
        const newData = cloneDeep(prev);

        // Iterate over each ERP data item.
        newData.forEach((erpDataItem) => {
          // Map over the position list to apply patches.
          // Update the position list for the current ERP data item.
          erpDataItem.positionList = applyArticlePricesOnPositions(
            erpDataItem.positionList
          );
        });
        // Return the new, patched data.
        return newData;
      });
    }

    // Only proceed if a patch exists and erpData has data.
    if (positionPatch && fetchedData.length) {
      // Update the data state.
      setPatchToData((prev) => {
        // Create a deep clone of the previous state to ensure immutability.
        const newData = cloneDeep(prev);

        // Iterate over each ERP data item.
        newData.forEach((erpDataItem) => {
          // Map over the position list to apply patches.
          const positionList = erpDataItem.positionList.map((position) => {
            // Find a matching patch for the current position.
            const patch = positionPatch.find(
              (x) =>
                'childs' in x.position // Check if the patch has 'childs' property.
                  ? (x.position.childs as number[]).some(
                      (y) => y === position.id
                    ) // If it has 'childs', check if any child ID matches.
                  : (x.position.originalId || x.position.id) === position.id // Otherwise, match by 'id' or 'originalId'.
            );

            // If a patch is found, apply the updates.
            if (patch) {
              return {
                ...position, // Keep existing position properties.
                supplier: patch.position.supplier,
                supplierId: patch.position.supplier?.id,
                singlePriceNet: patch.position.singlePriceNet,
                unit: patch.position.unit,
                totalPriceNet: patch.position.totalPriceNet,
                newAmount: patch.newAmount,
                newUnit: patch.newUnit,
                selected: patch.selected,
                currency: patch.currency,
                newSinglePrice: patch.position.singlePriceNet,
                originalDiscount: position.discount,
                newDiscount:
                  'originalDiscount' in position &&
                  position.originalDiscount !== undefined
                    ? position.originalDiscount
                    : patch.position.discount,
                discount:
                  'originalDiscount' in position &&
                  position.originalDiscount !== undefined
                    ? position.originalDiscount
                    : patch.position.discount,
              } as unknown as Position;
            }

            // If no patch is found, return the original position.
            return position;
          });
          // Update the position list for the current ERP data item.
          erpDataItem.positionList = positionList;
        });
        // Return the new, patched data.
        return newData;
      });
    }
  }, [
    positionPatch,
    fetchedData,
    setPatchToData,
    loading,
    applyArticlePricesOnPositions,
  ]);
};
